import { ClientFetch } from '../../fetch/ClientFetch';
import { HTTP_METHODS } from 'utils';
import type { SessionInfosResponse, SetSessionInfosParams } from '../../../types/session';
import type { UserDataResponse } from '../../../types/user';
import type { CartOverviewResponse } from '../../../types/cart';

/**
 * Browser counterparts of the server-side session helpers (`getSessionInfosServer`, `setSessionInfosServer`,
 * `getUserDataServer`, `getCartOverviewServer`), for pages that render without a server session.
 *
 * Every call goes through {@link ClientFetch}, which attaches the JWT from the cookie and refreshes it when
 * it is missing or expired. Callers that depend on order must `await ensureSession()` first, e.g. attribution
 * (`setSessionInfos`) has to reach the session the browser keeps rather than trigger a second anonymous login.
 *
 * Each method rejects when the request fails; callers bound the wait themselves (see `SESSION_REQUEST_TIMEOUT_MS`).
 */
export const sessionClient = {
  /**
   * Reads the attribution and voucher data stored on the current session.
   *
   * @returns The session infos: `pref`, `userHash` and, when present, `voucher` and `prefInfo`.
   */
  getSessionInfos: (): Promise<SessionInfosResponse> =>
    new ClientFetch().makePsiRequest<SessionInfosResponse>('auth.session.getSessionInfos', {}, { method: HTTP_METHODS.GET }),

  /**
   * Stores attribution data on the current session (`auth.session.setSessionInfos`).
   *
   * @param params - `pref` (partner ref), `voucher` and `referrer`; omitted fields are left unchanged.
   * @returns The session infos after the update.
   */
  setSessionInfos: (params: SetSessionInfosParams): Promise<SessionInfosResponse> =>
    new ClientFetch().makePsiRequest<SessionInfosResponse>('auth.session.setSessionInfos', params, {
      method: HTTP_METHODS.POST,
    }),

  /**
   * Loads the account data of a logged-in customer (`user.account.getData`).
   *
   * @param uuid - The customer UUID, taken from the `pixum-api-uuid` claim of the JWT.
   * @returns The raw user data response.
   */
  getUserData: (uuid: string): Promise<UserDataResponse> =>
    new ClientFetch().makePsiRequest<UserDataResponse>('user.account.getData', { uuid }, { method: HTTP_METHODS.GET }),

  /**
   * Loads the cart overview (`shoppingcart.cart.getOverview`), used for the header cart count.
   *
   * @param addPositions - Number of positions to add to the cart; pass `0` to only read it.
   * @returns The raw cart overview response.
   */
  getCartOverview: (addPositions: number): Promise<CartOverviewResponse> =>
    new ClientFetch().makePsiRequest<CartOverviewResponse>(
      'shoppingcart.cart.getOverview',
      { addPositions },
      { method: HTTP_METHODS.POST },
    ),
};
